/**
 * components/profile/AccountIdentityTab.jsx — v3.7.7 Phase 84.31
 *
 * The "Account & identity" tab of the unified Profile hub.
 *
 * What lives here:
 *   • First / last name
 *   • Email (read-only, from auth provider)
 *   • Date of birth — with the new hardening from Phase 84.31:
 *       – Never defaults to today (empty stays empty).
 *       – Rejects future dates.
 *       – Flags implausible ages (< 13 or > 110) for confirmation.
 *       – Displays the saved value in an unambiguous "13 Aug 1985" format
 *         alongside the native <input type=date> for editing.
 *       – Requires an explicit confirm dialog when the year of birth
 *         changes on an existing value, because that materially shifts
 *         age-based projections (retirement timing, DNA scoring, etc.).
 *   • Sign-in method (read-only summary; account security actions
 *     are surfaced when the provider supports them).
 *
 * What used to live here and MOVED (Phase 84.31):
 *   • Country / city / currency          → "Location & money" tab
 *   • DangerZone (delete all data)       → "Privacy & AI" tab
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { useConfirm } from '@/components/ConfirmDialog';
import {
  SectionHeader, SettingsCard, PrimaryButton, TextInput, FieldLabel, SavedFlash,
} from '@/components/settings/primitives';

// Age helper — returns null for missing or invalid strings.
function ageFromDOB(dobStr) {
  if (!dobStr) return null;
  const d = new Date(dobStr);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years -= 1;
  if (years < 0 || years > 130) return null;
  return years;
}

// Unambiguous "13 Aug 1985" formatter — locale-adaptive for the month
// name but always day-month-year so no user reads it as US m/d/y.
function prettyDOB(dobStr) {
  if (!dobStr) return '';
  const d = new Date(dobStr);
  if (Number.isNaN(d.getTime())) return '';
  const day   = d.getUTCDate();
  const month = d.toLocaleString(undefined, { month: 'short', timeZone: 'UTC' });
  const year  = d.getUTCFullYear();
  return `${day} ${month} ${year}`;
}

// Today's date in yyyy-mm-dd, used as the max attribute so the picker
// won't even allow a future date to be selected. Locales that render
// mm/dd/yy in the input still respect this max.
function todayISO() {
  const d = new Date();
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export default function AccountIdentityTab() {
  const { user, profile, refreshProfile } = useAuth();
  const confirm = useConfirm();

  const [form, setForm] = useState({ first_name: '', last_name: '', date_of_birth: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [dobError, setDobError] = useState('');

  // Hydrate from the loaded profile. Note: we NEVER seed date_of_birth
  // with today's date — an empty stored value stays empty in the UI.
  useEffect(() => {
    if (!profile) return;
    setForm({
      first_name:    profile.first_name || '',
      last_name:     profile.last_name || '',
      date_of_birth: (profile.date_of_birth || '').toString().slice(0, 10),
    });
  }, [profile]);

  const derivedAge = useMemo(() => ageFromDOB(form.date_of_birth), [form.date_of_birth]);
  const initial = (form.first_name || form.last_name || user?.email || '?').charAt(0).toUpperCase();

  const onDobChange = (v) => {
    // Empty is allowed — user is clearing it.
    if (!v) {
      setDobError('');
      setForm((f) => ({ ...f, date_of_birth: '' }));
      return;
    }
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) {
      setDobError('That date does not look valid.');
      return;
    }
    if (d.getTime() > Date.now()) {
      setDobError('Date of birth cannot be in the future.');
      // Do NOT set the value — surface the error but keep the previous one.
      return;
    }
    const age = ageFromDOB(v);
    if (age != null && (age < 13 || age > 110)) {
      // Non-blocking: allow, but warn.
      setDobError(`That works out to age ${age} — please double-check before saving.`);
    } else {
      setDobError('');
    }
    setForm((f) => ({ ...f, date_of_birth: v }));
  };

  const save = async () => {
    if (!user) return;
    // Guard: no future dates, ever, even if a browser bypassed the max.
    if (form.date_of_birth) {
      const d = new Date(form.date_of_birth);
      if (Number.isNaN(d.getTime()) || d.getTime() > Date.now()) {
        toast.error('Date of birth cannot be in the future.');
        return;
      }
    }
    // Confirm when the YEAR of an existing DOB is changing — that
    // materially shifts every age-based projection in the app.
    const prev = (profile?.date_of_birth || '').toString().slice(0, 10);
    if (prev && form.date_of_birth && prev.slice(0, 4) !== form.date_of_birth.slice(0, 4)) {
      const ok = await confirm({
        title: 'Update your date of birth?',
        description:
          'Changing your date of birth shifts retirement timing, work-optional projections and every age-based recommendation. Continue?',
        confirmLabel: 'Update date of birth',
        cancelLabel: 'Keep the current date',
      });
      if (!ok) return;
    }
    setSaving(true);
    const combined = [form.first_name, form.last_name].filter(Boolean).join(' ').trim();
    const payload = {
      first_name:    form.first_name,
      last_name:     form.last_name,
      name:          combined || null,
      date_of_birth: form.date_of_birth || null,
    };
    const { error } = await supabase.from('profiles').update(payload).eq('id', user.id);
    setSaving(false);
    if (error) {
      toast.error(error.message || 'Could not save changes.');
      return;
    }
    await refreshProfile?.();
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    toast.success('Profile updated.');
  };

  return (
    <div className="space-y-5" data-testid="profile-identity-tab">
      <SectionHeader
        title="Account & identity"
        subtitle="Your name, email address and date of birth. These stay with your account and are used for age-based projections only."
      />

      <SettingsCard className="px-6 py-6">
        {/* Avatar / name summary */}
        <div className="flex items-center gap-5 mb-6 pb-6 border-b border-border/60">
          <div className="w-16 h-16 rounded-full bg-evergreen flex items-center justify-center flex-shrink-0">
            <span className="font-display text-2xl font-medium text-white" aria-hidden="true">
              {initial}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-charcoal truncate">
              {[form.first_name, form.last_name].filter(Boolean).join(' ') || user?.email || 'Your name'}
            </p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <FieldLabel label="First name" />
            <TextInput
              value={form.first_name}
              onChange={(v) => setForm({ ...form, first_name: v })}
              placeholder="Your first name"
              testId="profile-first-name-input"
            />
          </div>
          <div>
            <FieldLabel label="Last name" />
            <TextInput
              value={form.last_name}
              onChange={(v) => setForm({ ...form, last_name: v })}
              placeholder="Your last name"
              testId="profile-last-name-input"
            />
          </div>
          <div>
            <FieldLabel label="Email address" />
            <TextInput value={user?.email || ''} onChange={() => {}} disabled />
            <p className="text-[11px] text-muted-foreground/70 mt-1.5">
              Email is managed through your sign-in provider.
            </p>
          </div>
          <div>
            <FieldLabel
              label={`Date of birth${derivedAge != null ? ` · Age ${derivedAge}` : ''}`}
              hint="Used to calculate age-based projections and retirement timing. Prefer not to share? Leave it blank."
            />
            <TextInput
              type="date"
              value={form.date_of_birth}
              onChange={onDobChange}
              max={todayISO()}
              testId="profile-dob-input"
            />
            {form.date_of_birth && !dobError && (
              <p
                className="text-[11px] text-muted-foreground/70 mt-1.5"
                data-testid="profile-dob-pretty"
              >
                Displayed as: <span className="font-medium text-charcoal">{prettyDOB(form.date_of_birth)}</span>
              </p>
            )}
            {dobError && (
              <p
                className="text-[11px] text-amber-700 dark:text-amber-400 mt-1.5 inline-flex items-center gap-1"
                role="alert"
                data-testid="profile-dob-error"
              >
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                {dobError}
              </p>
            )}
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <PrimaryButton onClick={save} disabled={saving} testId="profile-identity-save">
            {saving ? 'Saving…' : 'Save changes'}
          </PrimaryButton>
          <SavedFlash visible={saved} />
        </div>
      </SettingsCard>

      {/* Sign-in method — informational until we support multiple providers. */}
      <SettingsCard className="px-6 py-5">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-4">
          Sign-in method
        </p>
        <div className="flex items-center gap-3 py-2">
          <div className="w-8 h-8 rounded-lg bg-offwhite border border-border flex items-center justify-center text-xs font-semibold text-muted-foreground/70">
            @
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-charcoal">Email &amp; password</p>
            <p className="text-xs text-emerald-700 dark:text-emerald-400">Active</p>
          </div>
        </div>
      </SettingsCard>
    </div>
  );
}
